import { z } from 'zod';
export const DocumentCategorySchema = z.enum([
    'PROPUESTA',
    'ESPECIFICACION',
    'DIAGRAMA_ARQUITECTURA',
    'INFORME_ENTREGABLE',
    'SOW',
    'NDA',
    'CONTRATO',
    'BORRADOR',
    'CORREO',
    'IDENTIFICACION',
    'FACTURA_RECIBIDA',
    'JUSTIFICANTE_GASTO',
    'OTRO',
]);
export const DocumentCategoryLabel = {
    PROPUESTA: 'Propuesta',
    ESPECIFICACION: 'Especificación',
    DIAGRAMA_ARQUITECTURA: 'Diagrama de arquitectura',
    INFORME_ENTREGABLE: 'Informe / entregable',
    SOW: 'SOW',
    NDA: 'NDA',
    CONTRATO: 'Contrato',
    BORRADOR: 'Borrador',
    CORREO: 'Correo',
    IDENTIFICACION: 'Identificación',
    FACTURA_RECIBIDA: 'Factura recibida',
    JUSTIFICANTE_GASTO: 'Justificante de gasto',
    OTRO: 'Otro',
};
export const DocumentVersionDtoSchema = z.object({
    id: z.string(),
    version: z.number().int(),
    mimeType: z.string(),
    size: z.number().int(),
    sha256: z.string(),
    uploadedAt: z.string().datetime(),
    uploadedById: z.string().nullable(),
    uploadedByName: z.string().nullable(),
});
// Estado de indexación RAG. Duplicado del enum de Prisma para no acoplarlos.
export const DocIndexStatusSchema = z.enum([
    'PENDING',
    'PROCESSING',
    'DONE',
    'FAILED',
    'SKIPPED',
]);
export const DocumentListItemSchema = z.object({
    id: z.string(),
    fileName: z.string(),
    category: DocumentCategorySchema,
    tags: z.array(z.string()),
    opportunityId: z.string().nullable(),
    clientId: z.string().nullable(),
    latestVersion: z.number().int(),
    latestSize: z.number().int(),
    latestMimeType: z.string(),
    versionsCount: z.number().int(),
    createdAt: z.string().datetime(),
    createdByName: z.string().nullable(),
    indexStatus: DocIndexStatusSchema,
});
export const DocumentDetailSchema = DocumentListItemSchema.extend({
    notes: z.string().nullable(),
    versions: z.array(DocumentVersionDtoSchema),
});
/**
 * F41 — Datos estructurados que la IA extrae de una factura recibida. Todos
 * los campos son best-effort: si el LLM no consigue determinar alguno, viene
 * como null. Los importes viajan en céntimos y los porcentajes en base points
 * (bps: 2100 = 21%).
 */
export const InvoiceMetaSchema = z.object({
    supplier: z.string().nullable(),
    cif: z.string().nullable(),
    invoiceNumber: z.string().nullable(),
    issueDate: z.string().nullable(),
    baseCents: z.number().int().nullable(),
    vatRateBps: z.number().int().nullable(),
    vatCents: z.number().int().nullable(),
    irpfRateBps: z.number().int().nullable(),
    irpfCents: z.number().int().nullable(),
    totalCents: z.number().int().nullable(),
    currency: z.string().nullable(),
    /** 0-100. Baja confianza avisa al usuario. */
    confidence: z.number().int().min(0).max(100).nullable(),
});
export const AnalyzeInvoiceResponseSchema = z.object({
    /**
     * Motivo por el que la IA no pudo leer el documento, o null si fue bien. El
     * documento se guarda igual: esto NO es un error de la subida, es que los
     * campos vienen vacíos y hay que rellenarlos a mano.
     */
    aiError: z.string().nullable().default(null),
    /**
     * Otro documento con el MISMO fichero (misma huella sha256) que ya estaba
     * subido. No impide guardar —a veces se resube a propósito— pero se avisa.
     */
    duplicateOf: z
        .object({ id: z.string(), fileName: z.string(), createdAt: z.string() })
        .nullable()
        .default(null),
    document: z.object({
        id: z.string(),
        fileName: z.string(),
    }),
    extractedText: z.string(),
    invoiceMeta: InvoiceMetaSchema,
});
// F41+ — Listado filtrable de facturas recibidas (página OCR rediseñada).
export const ReceivedInvoiceExpenseSchema = z.object({
    id: z.string(),
    code: z.string(),
    status: z.enum(['PENDIENTE', 'PAGADO', 'ANULADO']),
    totalCents: z.number().int(),
    /** Retención practicada una vez confirmado el gasto. Manda sobre la del OCR. */
    irpfCents: z.number().int().default(0),
});
export const ReceivedInvoiceItemSchema = z.object({
    id: z.string(),
    fileName: z.string(),
    latestVersion: z.number().int(),
    createdAt: z.string().datetime(),
    createdByName: z.string().nullable(),
    indexStatus: DocIndexStatusSchema,
    /** true si la factura tiene metadatos interpretados por OCR/IA. */
    hasOcr: z.boolean(),
    invoiceMeta: InvoiceMetaSchema.nullable(),
    expense: ReceivedInvoiceExpenseSchema.nullable(),
});
export const ReceivedInvoicesResponseSchema = z.object({
    items: z.array(ReceivedInvoiceItemSchema),
    total: z.number().int(),
    page: z.number().int(),
    pageSize: z.number().int(),
    /** Años disponibles (por fecha de registro) para el selector de filtro. */
    years: z.array(z.number().int()),
});
//# sourceMappingURL=documents.js.map